<template>
  <section class="page-stack about-page about-story-page">
    <section class="about-story-hero">
      <div class="about-story-copy">
        <p class="eyebrow">About App</p>
        <h2>Raider.io Dashboard</h2>
        <p>
          A focused World of Warcraft workspace for raid progress, Mythic+ statistics,
          saved characters, build insights, Wowhead news and profile comparisons without
          jumping through a stack of browser tabs.
        </p>

        <div class="about-story-actions">
          <RouterLink class="btn" to="/raiding">
            <i class="fa-solid fa-shield-halved"></i>
            Explore raids
          </RouterLink>
          <RouterLink class="btn-outline" to="/favorites">
            <i class="fa-solid fa-star"></i>
            Save a character
          </RouterLink>
          <RouterLink class="btn-outline" to="/news">
            <i class="fa-solid fa-newspaper"></i>
            Read news
          </RouterLink>
        </div>
      </div>

      <div class="about-hero-shot" aria-label="Raider.io Dashboard overview screenshot">
        <header>
          <span></span>
          <span></span>
          <span></span>
          <strong>raider.io/dashboard</strong>
        </header>
        <section class="about-hero-metrics">
          <article>
            <span>Mythic+ score</span>
            <strong>3278</strong>
            <small>Tracked from Raider.io</small>
          </article>
          <article>
            <span>Raid progress</span>
            <strong>8/8</strong>
            <small>Current tier</small>
          </article>
          <article>
            <span>Build insights</span>
            <strong>Meta</strong>
            <small>Top leaderboard builds</small>
          </article>
        </section>
        <section class="about-live-example">
          <div>
            <span class="badge">Live workflow</span>
            <h3>+16 Eco-Dome Al'dani</h3>
            <p>Team roles, affixes, timer, score and source links in one compact card.</p>
          </div>
          <strong>312.4</strong>
        </section>
      </div>
    </section>

    <section class="about-story-section">
      <header class="about-section-head">
        <p class="eyebrow">Why it exists</p>
        <h3>Built for quick decisions, not tab management</h3>
        <p>
          Raider.io is the source of truth under the hood. This app reshapes that data into
          a dashboard for players who want fast character insight, dungeon statistics,
          gear trends, official news and side-by-side comparison inside one installable PWA.
        </p>
      </header>

      <div class="about-use-grid">
        <article v-for="useCase in useCases" :key="useCase.title" class="card about-use-card">
          <span class="about-icon"><i :class="useCase.icon"></i></span>
          <h4>{{ useCase.title }}</h4>
          <p>{{ useCase.description }}</p>
        </article>
      </div>
    </section>

    <section class="about-story-section">
      <header class="about-section-head">
        <p class="eyebrow">How it works</p>
        <h3>From profile URL to useful context</h3>
      </header>

      <div class="about-flow">
        <article v-for="step in flowSteps" :key="step.title" class="about-flow-step">
          <span>{{ step.index }}</span>
          <div>
            <h4>{{ step.title }}</h4>
            <p>{{ step.description }}</p>
          </div>
        </article>
      </div>
    </section>

    <section class="about-story-section">
      <header class="about-section-head">
        <p class="eyebrow">Screenshots</p>
        <h3>The main workflows at a glance</h3>
        <p>
          These example screens show what the dashboard is optimized for: raid progression,
          Mythic+ ladder statistics, gear trend review, Wowhead news, saved character profiles
          and comparison-ready summaries.
        </p>
      </header>

      <div class="about-screenshot-grid">
        <article v-for="screen in screenshots" :key="screen.title" class="about-screenshot-card">
          <div class="about-shot">
            <header>
              <span></span>
              <span></span>
              <span></span>
              <strong>{{ screen.windowTitle }}</strong>
            </header>
            <section :class="screen.layout">
              <div v-for="block in screen.blocks" :key="block.label" class="about-shot-block" :class="block.type">
                <small>{{ block.label }}</small>
                <strong>{{ block.value }}</strong>
              </div>
            </section>
          </div>
          <div>
            <h4>{{ screen.title }}</h4>
            <p>{{ screen.description }}</p>
          </div>
        </article>
      </div>
    </section>

    <section class="about-story-section about-data-section">
      <div>
        <p class="eyebrow">Data flow</p>
        <h3>One app shell for the tools players already use</h3>
        <p>
          The dashboard keeps Raider.io data central, then places useful external destinations
          next to the context where they matter: logs, official armory pages, Wowhead articles,
          item tooltips, affixes, talent calculators and RSS news.
        </p>
      </div>

      <div class="about-data-map" aria-label="Application data sources">
        <article v-for="source in dataSources" :key="source.name" class="about-data-node">
          <span class="about-source-icon">
            <img v-if="source.image" :src="source.image" :alt="source.name" loading="lazy">
            <i v-else :class="source.icon"></i>
          </span>
          <strong>{{ source.name }}</strong>
          <small>{{ source.copy }}</small>
        </article>
      </div>
    </section>

    <section class="about-story-section">
      <header class="about-section-head">
        <p class="eyebrow">Alternatives</p>
        <h3>You can also visit these apps and sites</h3>
      </header>

      <div class="alternative-list">
        <a
          v-for="alternative in alternatives"
          :key="alternative.href"
          :href="alternative.href"
          target="_blank"
          rel="noreferrer"
          class="alternative-link"
        >
          <span>
            <strong>{{ alternative.name }}</strong>
            <small>{{ alternative.description }}</small>
          </span>
          <i class="fa-solid fa-arrow-up-right-from-square"></i>
        </a>
      </div>
    </section>

    <section class="about-story-section">
      <header class="about-section-head">
        <p class="eyebrow">Dashboard Philosophy</p>
        <h3>Focused insight on top of Raider.io data</h3>
        <p>
          The dashboard keeps its original promise: a concise, app-like place for the
          character and progression context players check most often.
        </p>
      </header>

      <div class="about-grid">
        <article class="card about-card">
          <header>
            <p class="eyebrow">Purpose</p>
            <h3>One place for character insight</h3>
          </header>
          <p>
            The application gives you access to the most important information about a character in one
            place: progression, equipment, special effects, Mythic+ score, rankings, raid results and
            recent activity. It also adds broader context around the current season with dungeon
            statistics, gear trends and official Wowhead updates.
          </p>
          <p>
            It is built for players who want a concise dashboard for their own characters, friends,
            favorite leaders or characters they would like to follow over time.
          </p>
        </article>

        <article class="card about-card">
          <header>
            <p class="eyebrow">Raider.io Data</p>
            <h3>How it differs from Raider.io</h3>
          </header>
          <p>
            Raider.io is the source of truth under the hood. This app uses Raider.io data, then presents
            the most relevant character details in a more compact, focused and app-like form.
          </p>
          <p>
            Instead of opening several profile sections, you can review character progress, gear,
            rankings, dungeon runs, leaderboard statistics, build insights, news and saved lists from
            a single workspace.
          </p>
        </article>
      </div>
    </section>

    <section class="about-story-section about-cta">
      <div>
        <p class="eyebrow">Start here</p>
        <h3>Open a live view or build your own watchlist</h3>
        <p>
          Check current raid rankings, inspect Mythic+ statistics, review gear trends, read the
          latest Wowhead updates, save a character by Raider.io URL, then compare your favorites
          whenever you need a clearer roster picture.
        </p>
      </div>
      <div class="about-story-actions">
        <RouterLink class="btn" to="/mythic-plus">
          <i class="fa-solid fa-dungeon"></i>
          Mythic+
        </RouterLink>
        <RouterLink class="btn-outline" to="/compare">
          <i class="fa-solid fa-scale-balanced"></i>
          Compare
        </RouterLink>
        <RouterLink class="btn-outline" to="/gear-insights">
          <i class="fa-solid fa-shirt"></i>
          Builds
        </RouterLink>
      </div>
    </section>
  </section>
</template>

<script>
import rioServiceIcon from '../assets/images/services/rio-small-logo.svg'
import warcraftLogsServiceIcon from '../assets/images/services/warcraftlogs-small-logo.png'
import wowServiceIcon from '../assets/images/services/wow-small-logo.svg'

export default {
  name: 'AboutView',
  data() {
    return {
      useCases: [
        {
          title: 'Evaluate characters',
          description: 'Review score, item level, guild, raid progress, rankings, best runs and profile links from a single page.',
          icon: 'fa-solid fa-id-card',
        },
        {
          title: 'Create watchlists',
          description: 'Save friends, leaders or recruitment candidates and refresh their profiles when you need current context.',
          icon: 'fa-solid fa-star',
        },
        {
          title: 'Compare profiles',
          description: 'Place saved characters side by side to understand differences in score, gear, ranks and raid progress.',
          icon: 'fa-solid fa-code-compare',
        },
        {
          title: 'Follow the meta',
          description: 'Browse raid rankings, Mythic+ ladder statistics and aggregated build trends from top leaderboard characters.',
          icon: 'fa-solid fa-ranking-star',
        },
        {
          title: 'Read Warcraft updates',
          description: 'Follow Wowhead news, Retail and Classic feeds, blue posts, translated summaries and full article pages.',
          icon: 'fa-solid fa-newspaper',
        },
      ],
      flowSteps: [
        {
          index: '01',
          title: 'Choose a view',
          description: 'Open raids, Mythic+, Build Insights, News, favorites or compare depending on the question you need to answer.',
        },
        {
          index: '02',
          title: 'Load reliable data',
          description: 'The app requests Raider.io data for seasons, rankings, runs and profiles, plus Wowhead RSS and article content.',
        },
        {
          index: '03',
          title: 'Act from context',
          description: 'Use compact cards, charts, tables, gear views, source links, translations and local lists to make the next decision faster.',
        },
      ],
      screenshots: [
        {
          title: 'Raid Progression',
          windowTitle: 'raiding',
          description: 'Region and difficulty filters, guild rankings, boss progress and source links for the selected raid.',
          layout: 'about-shot-raid',
          blocks: [
            { label: 'Guilds loaded', value: '50', type: 'wide' },
            { label: 'Bosses', value: '8', type: '' },
            { label: 'Pulls', value: '1.2k', type: '' },
            { label: 'Full clears', value: '14', type: 'accent' },
          ],
        },
        {
          title: 'Mythic+ Runs',
          windowTitle: 'mythic-plus',
          description: 'Statistics-first view with timed key charts by dungeon, class distribution, run cards and Raider.io links.',
          layout: 'about-shot-list',
          blocks: [
            { label: 'Stats', value: '+16 keys', type: 'accent' },
            { label: 'Dungeons', value: 'Timed chart', type: '' },
            { label: 'Classes', value: 'Role mix', type: '' },
          ],
        },
        {
          title: 'Build Insights',
          windowTitle: 'gear-insights',
          description: 'Aggregated gear, talent and stat-signal trends from top Mythic+ leaderboard characters.',
          layout: 'about-shot-list',
          blocks: [
            { label: 'Spec', value: 'All roles', type: 'accent' },
            { label: 'Builds', value: 'Top picks', type: '' },
            { label: 'Cache', value: 'Local', type: '' },
          ],
        },
        {
          title: 'News',
          windowTitle: 'news',
          description: 'Wowhead RSS feeds, blue tracker posts, category tabs, article pages, media embeds and client-side translation.',
          layout: 'about-shot-list',
          blocks: [
            { label: 'Feed', value: 'All News', type: 'accent' },
            { label: 'Blue', value: 'Tracker', type: '' },
            { label: 'Lang', value: 'EN / UA / RU', type: '' },
          ],
        },
        {
          title: 'Favorites',
          windowTitle: 'favorites',
          description: 'Saved local profiles with quick refresh, gear panel, talent view and best run details.',
          layout: 'about-shot-list',
          blocks: [
            { label: 'RIO', value: '3211', type: 'accent' },
            { label: 'ilvl', value: '714', type: '' },
            { label: 'Raid', value: '7/8 M', type: '' },
            { label: 'Realm', value: 'EU', type: '' },
          ],
        },
      ],
      dataSources: [
        {
          name: 'Raider.io',
          copy: 'Raid, Mythic+, profile and gear source data',
          image: rioServiceIcon,
        },
        {
          name: 'Warcraft Logs',
          copy: 'Fast path to combat logs',
          image: warcraftLogsServiceIcon,
        },
        {
          name: 'World of Warcraft',
          copy: 'Official armory profiles',
          image: wowServiceIcon,
        },
        {
          name: 'Wowhead',
          copy: 'News, blue posts, articles, items, spells and tooltips',
          icon: 'fa-solid fa-wand-magic-sparkles',
        },
      ],
      alternatives: [
        {
          name: 'Raider.IO',
          description: 'Character profiles, Mythic+ rankings, raid progression and leaderboards.',
          href: 'https://raider.io/',
        },
        {
          name: 'Warcraft Logs',
          description: 'Combat logs, parses, rankings and encounter performance analysis.',
          href: 'https://www.warcraftlogs.com/',
        },
        {
          name: 'World of Warcraft Armory',
          description: 'Official Blizzard character profiles, collections and account-linked character data.',
          href: 'https://worldofwarcraft.blizzard.com/',
        },
        {
          name: 'Wowhead',
          description: 'Warcraft news, blue tracker posts, guides, item pages and database tooltips.',
          href: 'https://www.wowhead.com/',
        },
      ],
    }
  },
}
</script>
